import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo). "Chats Unificados",
// API oficial e parceria Meta são [só site]: entram como copy, não como prova.
// A keyword é multi-loja; "cobrança via WhatsApp" pertence a /cobranca-whatsapp.
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";
const SOURCE_PEGN = "Pequenas Empresas & Grandes Negócios, ago/2026";

export const whatsappParaRedeDeLojas: SeoLandingConfig = {
  path: "/whatsapp-para-rede-de-lojas",
  kind: "ferramenta",
  keyword: "WhatsApp para rede de lojas",
  title: "WhatsApp para rede de lojas num chat unificado",
  description:
    "Junte os números de WhatsApp de todas as lojas da rede num chat unificado, com o atendimento separado por loja e a API oficial do WhatsApp, parceira da Meta.",
  breadcrumbLabel: "WhatsApp para rede de lojas",
  updatedAt: "2026-10-17",
  service: {
    name: "WhatsApp unificado para redes de lojas",
    serviceType:
      "Chat unificado de múltiplos números de WhatsApp com atendimento separado por loja",
    audience: "Redes de lojas do varejo com crediário próprio",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Para redes de 4 a 20 lojas",
      title: "WhatsApp para rede de lojas, com todos os números num chat só",
      lead: "Cada unidade mantém o próprio número, e a equipe responde a todas as conversas numa tela única, separadas por loja, pela API oficial do WhatsApp.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Entender o chat unificado",
      secondaryHref: "#como-funciona",
      reassurance: "Um especialista mostra o chat unificado de graça.",
    },
    {
      type: "proof",
      title: "Redes e lojas que falam com os clientes pela Fidexa",
      clientNames: [
        "Grupo Minas",
        "Tangará",
        "Dantom Multimarcas",
        "Lojas Elder",
        "Omara Calçados",
        "Lojas Rosário",
      ],
    },
    {
      type: "shortAnswer",
      id: "o-que-e",
      title: "Como organizar o WhatsApp de uma rede de lojas",
      answer:
        "Na Fidexa, os vários números de WhatsApp de uma rede ficam num único chat unificado, que separa o atendimento por loja. O envio usa a API oficial do WhatsApp, em modelos de template, e o crediário das unidades aparece num dashboard único. Ninguém precisa revezar celulares entre as lojas.",
      bullets: [
        "Cada loja segue com o número que o cliente já conhece.",
        "A equipe de cada loja responde e negocia no mesmo chat.",
        "Pelo Fidexa Pay, o cliente paga a parcela por Pix sozinho.",
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Como a rede passa a usar um WhatsApp só",
      steps: [
        {
          title: "Os números entram na Fidexa",
          text: "Os WhatsApps das unidades são conectados pela API oficial, e cada um continua identificado com a sua loja.",
        },
        {
          title: "Um chat para a rede inteira",
          text: "As conversas de todas as lojas chegam ao mesmo lugar, divididas por unidade, sem misturar o cliente de uma com o de outra.",
        },
        {
          title: "A equipe responde por loja",
          text: "Quem atende vê de que unidade é cada conversa e responde, tira dúvidas e fecha acordos sem pegar outro aparelho.",
        },
        {
          title: "A gestão olha a rede de cima",
          text: "O dashboard único mostra as pendências de cada unidade, e a análise de conversão mede o desempenho das mensagens.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparativo",
      title: "Um celular por loja, WhatsApp Business ou Fidexa",
      lead: "Três formas de uma rede atender pelo WhatsApp.",
      caption:
        "Comparação entre manter um celular por loja, usar o WhatsApp Business comum e usar o chat unificado da Fidexa numa rede de lojas",
      columns: [
        { key: "celular", label: "Um celular por loja" },
        { key: "business", label: "WhatsApp Business comum" },
        { key: "fidexa", label: "Fidexa", highlight: true },
      ],
      rows: [
        {
          label: "Uma tela só para os chats da rede",
          cells: { celular: false, business: false, fidexa: true },
        },
        {
          label: "Atendimento separado por loja",
          cells: {
            celular: "Cada aparelho é uma loja",
            business: "Cada aparelho é uma loja",
            fidexa: "No mesmo chat, dividido por unidade",
          },
        },
        {
          label: "Canal das mensagens em massa",
          cells: {
            celular: "Envio manual, um a um",
            business: "Aplicativo do celular",
            fidexa: "API oficial, com templates aprovados",
          },
        },
        {
          label: "Quem negocia o atraso",
          cells: {
            celular: "Quem estiver com o aparelho",
            business: "Quem estiver com o aparelho",
            fidexa: "Atendente de cada unidade, num chat único",
          },
        },
        {
          label: "Cliente paga sozinho fora do expediente",
          cells: {
            celular: false,
            business: false,
            fidexa: "Pix pelo Fidexa Pay, 24 horas",
          },
        },
        {
          label: "Visão das pendências de cada loja",
          cells: {
            celular: false,
            business: false,
            fidexa: "Dashboard único",
          },
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros",
      title: "O WhatsApp da Fidexa em números",
      metrics: [
        {
          value: "15 mil",
          label: "Mensagens enviadas por dia, aproximadamente",
          source: SOURCE_ERP_NV,
        },
        {
          value: "98%",
          label: "Taxa de visualização e resposta das mensagens",
          source: `${SOURCE_PEGN}, segundo Bruno Reis`,
        },
        {
          value: "480.345",
          label: "Acordos de pagamento fechados",
          source: "Plataforma Fidexa",
        },
      ],
    },
    {
      type: "faq",
      id: "perguntas",
      title: "Perguntas frequentes sobre WhatsApp para rede de lojas",
      items: [
        {
          question: "Dá para juntar o WhatsApp de várias lojas num lugar só?",
          answer:
            "Sim. Na Fidexa, os números de WhatsApp das lojas da rede são controlados num único chat unificado, e quem acompanha vê as conversas de todas as unidades na mesma tela.",
        },
        {
          question: "Cada loja continua com o próprio número?",
          answer:
            "Continua. A Fidexa não troca os números das unidades por um só: ela reúne os vários números no chat unificado e separa o atendimento por loja.",
        },
        {
          question:
            "Qual a diferença entre o WhatsApp Business e a API oficial do WhatsApp?",
          answer:
            "O WhatsApp Business comum é o aplicativo usado no celular da loja. A API oficial é o canal da Meta para empresas que enviam mensagens por um sistema, com modelos de template aprovados. A Fidexa usa a API oficial.",
        },
        {
          question: "A Fidexa é parceira da Meta?",
          answer:
            "Sim. A Fidexa é parceira Meta e usa a API oficial do WhatsApp. À PEGN, o fundador Bruno Reis explicou: “Temos selo de autenticação da Meta, porque trabalhamos no WhatsApp em conformidade com o modelo de template”.",
        },
        {
          question: "Usar a API oficial protege os números das lojas?",
          answer:
            "É o caminho que a própria Meta prevê para empresas que mandam muitas mensagens. Com templates aprovados pela Meta, a rede deixa de depender de disparos feitos pelo aplicativo comum de cada loja.",
        },
        {
          question: "Para que a rede usa o WhatsApp dentro da Fidexa?",
          answer:
            "Para tudo o que envolve o crediário próprio: lembrete automático da parcela que vai vencer, negociação do atraso feita pela equipe da loja e pagamento por Pix na conversa, pelo Fidexa Pay, além da recuperação de dívidas antigas.",
        },
        {
          question:
            "O que acontece quando o cliente escreve com a loja fechada?",
          answer:
            "Ninguém precisa responder na hora. A mensagem fica no chat unificado, na fila da loja certa, para a equipe atender no expediente. Enquanto isso, o cliente que só quer pagar usa o Fidexa Pay e quita a parcela por Pix a qualquer hora.",
        },
        {
          question: "Como a gestão acompanha o que cada loja recebe?",
          answer:
            "Pelo dashboard único, que reúne a carteira da rede inteira e aponta o que cada unidade tem a receber, e pela análise de conversão, que mede o desempenho das mensagens.",
        },
        {
          question: "Para quantas lojas a Fidexa foi pensada?",
          answer:
            "O foco são redes de 4 a 20 lojas, mas a Fidexa atende da loja única até redes com várias unidades.",
        },
        {
          question: "A rede precisa mudar de ERP para usar a Fidexa?",
          answer:
            "Não. A Fidexa trabalha ao lado do ERP da rede: lê a carteira do TOTVS Linx por integração ou recebe a planilha exportada de Outros ERPs.",
        },
        {
          question: "Qual o preço do chat unificado para uma rede?",
          answer:
            "O valor depende do tamanho da operação; numa rede, o número de unidades pesa. Na demonstração gratuita, você vê o chat unificado montado com as unidades da sua rede e recebe a proposta de um especialista.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final",
      title: "Veja os números de todas as suas lojas num chat só",
      text: "Na demonstração gratuita, um especialista monta o exemplo com as unidades da sua rede.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
